import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchBar, fetchOchered, fetchRazbor, type OcheredFiltry, type Razbor } from './api';
import { useFilialShapki } from '../multi-tenant/hooks';

/**
 * ⛔ ЖИВОСТЬ — ТЕМ ЖЕ МЕХАНИЗМОМ, ЧТО У БАРА ТЕЛЕФОНИИ, И ЭТО РЕШЕНИЕ (`s603`).
 *
 * Бар телефонии живёт опросом TanStack Query (`useWorkplaceState`, 2 сек), и
 * поток событий (SSE/WebSocket) там отвергнут ЗАМЕРОМ, а не вкусом: php-fpm
 * держит пять рабочих процессов на всю админку, открытый поток забирает один
 * насовсем, и три дежурных положили бы панель. Новый канал обновления здесь
 * упёрся бы ровно в тот же пул — поэтому его нет.
 *
 * ⛔ `refetchIntervalInBackground: false` — вкладка в фоне не опрашивается: у
 * владельца админка открыта сутками, и фоновый опрос жёг бы те же процессы
 * впустую.
 */
const OCHERED_MS = 5_000;

/**
 * ⛔⛔ БАР ЖИВЁТ ТЕМ ЖЕ ТАКТОМ, ЧТО ОЧЕРЕДЬ (владелец, 15.09, п.15: «бар сверху
 * не обновляется в реалтайме, висит со старой инфой без перезагрузки экрана»).
 *
 * `s603` обещала владельцу «тем же механизмом, что телефония», и механизм
 * действительно тот же — опрос. Но такт был СВОЙ, втрое реже: телефония 2 с,
 * очередь 5 с, бар 15 с. Владелец писал боту и пятнадцать секунд смотрел на
 * старое число — «не обновляется» и «обновляется втрое медленнее, чем всё
 * рядом» на глаз неотличимы.
 *
 * Обещание проверяется числом, а не словом: такт бара = такт очереди.
 */
const BAR_MS = OCHERED_MS;

export const OCHERED_KEY = ['obrashcheniya', 'ochered'] as const;
export const BAR_KEY = ['obrashcheniya', 'bar'] as const;
/** Ключ бара по филиалу шапки (`s669`); `BAR_KEY` — общий префикс для сброса. */
export const barKey = (filialId: number | null) => [...BAR_KEY, filialId ?? 'vse'] as const;
export const razborKey = (id: number) => ['obrashcheniya', 'razbor', id] as const;

export function useOchered(filtry: OcheredFiltry, enabled = true) {
  const qc = useQueryClient();

  const zapros = useQuery({
    queryKey: [...OCHERED_KEY, filtry],
    queryFn: () => fetchOchered(filtry),
    enabled,
    refetchInterval: OCHERED_MS,
    refetchIntervalInBackground: false,
    // Прошлый ответ держим на экране, пока едет новый: иначе список моргал бы
    // пустотой каждые пять секунд и «всё отвечено» мелькало бы при полной очереди.
    placeholderData: (prev) => prev,
    retry: false,
  });

  /*
   * ⛔⛔ СЧЁТЧИК ШАПКИ И СТРОКИ СПИСКА — ИЗ ОДНОГО ЖИВОГО ОПРОСА
   * (владелец, приёмка 18.09.2026, `s614`).
   *
   * Сервер давно везёт сводку бара ВНУТРИ ответа очереди (`OcheredOtvet.bar`,
   * `s605`) — ровно чтобы оба числа относились к одному мигу. Но шапка её не
   * читала и жила своим запросом со своим тактом: два источника одной величины,
   * расходящиеся на такт. Владелец видел это как «сверху обновилось, а список
   * нет» — сверху и снизу отвечали РАЗНЫЕ опросы.
   *
   * Связка стоит ЗДЕСЬ, в самом хуке очереди, а не на одном экране (§33.2):
   * кто бы ни спросил очередь, шапка получает её же миг. Свой запрос бара не
   * снят — он кормит шапку на всех остальных экранах панели, где очереди нет.
   */
  const svodka = zapros.data?.bar;
  useEffect(() => {
    if (svodka !== undefined) qc.setQueryData(barKey(filtry.filial_id ?? null), svodka);
  }, [svodka, qc, filtry.filial_id]);

  return zapros;
}

/** s687: load every page while preserving the server selection and order. */
export function useMessengerOchered(filtry: OcheredFiltry, enabled = true) {
  const qc = useQueryClient();
  const zapros = useInfiniteQuery({
    queryKey: [...OCHERED_KEY, 'messenger', filtry],
    queryFn: ({ pageParam }) => fetchOchered({ ...filtry, stranica: pageParam }),
    initialPageParam: 1,
    getNextPageParam: last => last.pagination?.sleduyushchaya ?? undefined,
    placeholderData: (previous, previousQuery) => {
      const prior = previousQuery?.queryKey.at(-1) as OcheredFiltry | undefined;
      return prior?.filial_id === filtry.filial_id ? previous : undefined;
    },
    enabled, refetchInterval: OCHERED_MS, refetchIntervalInBackground: false, retry: false,
  });
  const data = useMemo(() => {
    if (!zapros.data?.pages.length) return undefined;
    const seen = new Set<number>();
    return { ...zapros.data.pages[0], stroki: zapros.data.pages.flatMap(p => p.stroki).filter(s => { if (seen.has(s.id)) return false; seen.add(s.id); return true; }) };
  }, [zapros.data]);
  const bar = data?.bar;
  useEffect(() => { if (bar) qc.setQueryData(barKey(filtry.filial_id),bar); }, [bar,qc,filtry.filial_id]);
  return { ...zapros, data };
}

/*
 * ⛔⛔ ФИЛИАЛ ПЕРЕПИСКИ — ПО КЛИЕНТКЕ, ВЫБОР — В ШАПКЕ (владелец, 17.10.2026, `s851`:
 * «переписки у нас по филиалам делились не по номерам, а по принадлежности клиента»).
 *
 * Прежде (`s694`, 04.10) шапка переписки не трогала: беседа линии сети без филиала
 * пропадала из-под выбранного филиала. Теперь сервер определяет филиал беседы по
 * клиентке (запись → заявка → её филиал), а беседа, чей филиал не определился,
 * видна под любым филиалом — сообщение не теряется.
 *
 * Очередь и бар обязаны брать филиал из ЭТОГО места оба: разойдутся — в шапке
 * снова будет «ждёт 152 ч» при самом старом 30 ч в очереди (`s669`).
 */
export function useFilialPerepisok(): number | null {
  return useFilialShapki();
}

export function useBarObrashcheniy(enabled: boolean) {
  const filialId = useFilialPerepisok();
  return useQuery({
    queryKey: barKey(filialId),
    queryFn: () => fetchBar(filialId),
    enabled,
    refetchInterval: BAR_MS,
    refetchIntervalInBackground: false,
    retry: false,
  });
}

export function useRazbor(id: number | null) {
  return useQuery({
    queryKey: razborKey(id ?? 0),
    queryFn: () => fetchRazbor(id as number),
    enabled: id !== null,
    refetchInterval: OCHERED_MS,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev?.id === id ? prev : undefined,
    retry: false,
  });
}

/**
 * Обвязка хода: выполнить, положить свежую раскрытую строку в кэш и освежить
 * очередь с баром.
 *
 * ⛔ Ответ хода — ИСТИНА, а не «наверное получилось»: сервер вернул состояние,
 * которое легло в базу, и именно его видит дежурный.
 */
export function useHod(id: number | null) {
  const qc = useQueryClient();
  const [idyot, setIdyot] = useState(false);
  const [otkaz, setOtkaz] = useState<string | null>(null);

  const vypolnit = useCallback(
    async (deystvie: () => Promise<Razbor>, priOtkaze?: (kod: string | null, status: number | null) => void) => {
      if (id === null) return null;
      setIdyot(true);
      setOtkaz(null);
      try {
        const svezhiy = await deystvie();
        qc.setQueryData(razborKey(id), svezhiy);
        void qc.invalidateQueries({ queryKey: OCHERED_KEY });
        void qc.invalidateQueries({ queryKey: BAR_KEY });
        return svezhiy;
      } catch (e: unknown) {
        // Отказ машины говорит СВОИМИ словами (окно суток, чужой держатель,
        // нет права) — подменять его общим «что-то пошло не так» запрещено.
        const soobshchenie =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? null;
        setOtkaz(soobshchenie ?? 'Ход не прошёл — попробуйте ещё раз.');
        // `s713`: код отказа свода отправок и стража — тому, кто умеет на него ответить.
        const otvet = (e as { response?: { status?: number; data?: { kod?: string } } })?.response;
        priOtkaze?.(otvet?.data?.kod ?? null, otvet?.status ?? null);
        return null;
      } finally {
        setIdyot(false);
      }
    },
    [id, qc],
  );

  return { vypolnit, idyot, otkaz, sbrositOtkaz: () => setOtkaz(null) };
}

/**
 * Задержка перед вопросом серверу: поле живёт без неё, сервер спрашивается
 * отложенно. Иначе каждый символ поиска гонял бы запрос и терял курсор.
 */
export function useZaderzhka<T>(znachenie: T, ms = 350): T {
  const [otlozhennoe, setOtlozhennoe] = useState(znachenie);

  useEffect(() => {
    const t = setTimeout(() => setOtlozhennoe(znachenie), ms);
    return () => clearTimeout(t);
  }, [znachenie, ms]);

  return otlozhennoe;
}
